interface Props {
  /** Cards at high mastery (the learned count). */
  learned: number
  /** Total cards in the deck. */
  total: number
  /** Track height in px. Intentionally thinner than MasteryMeter (3 vs 6px). */
  height?: number
  /** Track width — full-width by default. Pass a px number for a fixed size. */
  width?: number | string
  className?: string
}

/**
 * Continuous unlabeled deck-level progress hairline (Wave 3 §1 / Wave 4 §2).
 *
 * Encodes a RATIO (learned / total) — a percentage, not a discrete mastery
 * level. No tick marks: they would incorrectly imply 8 discrete states where
 * none exist (Wave 3 §1 amendment: tick-default rule applies to the per-word
 * indicator only; deck-level bars must stay smooth and continuous).
 *
 * Uses the same --mastery-fill-* / --mastery-track tokens as MasteryMeter so
 * the two surfaces read as a coherent system. Fill is always --mastery-fill-high
 * ("earned") regardless of the exact ratio — the ratio is carried by width only.
 */
export default function DeckProgressBar({
  learned,
  total,
  height = 3,
  width = '100%',
  className,
}: Props) {
  const pct = total > 0 ? Math.min(100, Math.max(0, (learned / total) * 100)) : 0

  return (
    <div
      className={className}
      role="img"
      aria-label={`${Math.round(pct)}% of deck learned`}
      title={`${learned} / ${total} cards learned`}
      style={{
        position: 'relative',
        width,
        height,
        flexShrink: 0,
        background: 'var(--mastery-track)',
        borderRadius: 'var(--radius-pill)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: `${pct}%`,
          height: '100%',
          background: 'var(--mastery-fill-high)',
          borderRadius: 'var(--radius-pill)',
          transition: 'width 0.4s ease',
        }}
      />
    </div>
  )
}
